import { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { ArrowsLeftRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function TransferCareDialog({
  open,
  onOpenChange,
  patientId,
  providers,
  token,
  onTransferComplete,
  saveBeforeTransfer,
}) {
  const [selectedProvider, setSelectedProvider] = useState('');
  const [transferPin, setTransferPin] = useState('');
  const [pinInput, setPinInput] = useState('');
  const [transferStep, setTransferStep] = useState('select');
  const [transferLoading, setTransferLoading] = useState(false);

  const resetDialog = () => {
    setTransferStep('select');
    setSelectedProvider('');
    setTransferPin('');
    setPinInput('');
  };

  const handleClose = () => {
    onOpenChange(false);
    resetDialog();
  };

  const handleInitiateTransfer = async () => {
    setTransferLoading(true);
    try {
      let pid = patientId;
      if (!pid && saveBeforeTransfer) {
        pid = await saveBeforeTransfer();
        if (!pid) { setTransferLoading(false); return; }
        toast.success('Patient saved before transfer');
      }
      const { data } = await axios.post(
        `${API_URL}/api/patients/${pid}/transfer/initiate`,
        { target_user_id: selectedProvider },
        {}
      );
      setTransferPin(data.pin);
      setTransferStep('pin-generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate transfer');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleConfirmTransfer = async () => {
    setTransferLoading(true);
    try {
      const pid = patientId || (saveBeforeTransfer ? null : null);
      await axios.post(
        `${API_URL}/api/patients/${pid}/transfer/accept`,
        { pin: pinInput },
        {}
      );
      toast.success('Care transferred successfully!');
      handleClose();
      onTransferComplete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid PIN or transfer failed');
    } finally {
      setTransferLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <ArrowsLeftRight size={24} className="text-purple-600" />
            Transfer Patient Care
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-slate-600">
            {transferStep === 'select' && 'Select the provider to transfer care to. The patient will be saved automatically.'}
            {transferStep === 'pin-generated' && 'Share this PIN with the receiving provider. They must enter it to confirm the transfer.'}
            {transferStep === 'enter-pin' && 'Enter the PIN to confirm the care transfer.'}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {transferStep === 'select' && (
          <div className="space-y-3 py-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Select Provider</Label>
            <Select value={selectedProvider} onValueChange={setSelectedProvider}>
              <SelectTrigger className="rounded-sm border-slate-300" data-testid="transfer-provider-select">
                <SelectValue placeholder="Choose a provider..." />
              </SelectTrigger>
              <SelectContent>
                {providers.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {transferStep === 'pin-generated' && (
          <div className="space-y-4 py-2 text-center">
            <p className="text-sm text-slate-600">Transfer PIN Code:</p>
            <div className="text-4xl font-mono font-black tracking-[0.5em] text-purple-700 bg-purple-50 rounded-lg py-4" data-testid="transfer-pin-display">
              {transferPin}
            </div>
            <p className="text-xs text-slate-500">This PIN expires in 10 minutes.</p>
            <Button
              onClick={() => setTransferStep('enter-pin')}
              className="w-full bg-purple-600 hover:bg-purple-700 rounded-sm"
              data-testid="proceed-enter-pin-button"
            >
              Enter PIN to Confirm
            </Button>
          </div>
        )}

        {transferStep === 'enter-pin' && (
          <div className="space-y-3 py-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Enter PIN Code</Label>
            <Input
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="rounded-sm border-slate-300 text-center text-2xl font-mono tracking-[0.3em]"
              maxLength={6}
              data-testid="transfer-pin-input"
            />
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleClose} data-testid="cancel-transfer-dialog">
            Cancel
          </AlertDialogCancel>
          {transferStep === 'select' && (
            <Button
              disabled={!selectedProvider || transferLoading}
              onClick={handleInitiateTransfer}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="initiate-transfer-button"
            >
              {transferLoading ? 'Generating...' : 'Generate PIN'}
            </Button>
          )}
          {transferStep === 'enter-pin' && (
            <Button
              disabled={pinInput.length !== 6 || transferLoading}
              onClick={handleConfirmTransfer}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-transfer-button"
            >
              {transferLoading ? 'Transferring...' : 'Confirm Transfer'}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
